import dayjs from 'dayjs';

import type { MarketplaceRootData } from '../schemas/rootSchema';
import {
  AcquisitionType,
  CopyType,
  LandingPageCopySection,
  MarketplaceOffer,
  MarketplaceOfferGroup,
  MarketplacePayload,
  MarketplaceProduct,
  TargetMarket,
} from '../types/types';

type AudienceKey = 'subscriber' | 'freemium';

function buildOfferGroupsFromAudience(
  audience: MarketplaceRootData['configuration']['subscriber' | 'freemium'],
  targetMarket: TargetMarket,
  acquisitionTypes: AcquisitionType[]
): MarketplaceOfferGroup[] {
  const groups: MarketplaceOfferGroup[] = [];

  if (
    acquisitionTypes.includes(AcquisitionType.Purchase) &&
    audience.purchase?.basePaymentOfferId
  ) {
    const offer: MarketplaceOffer = {
      basePaymentOfferId: audience.purchase.basePaymentOfferId,
      targetMarket,
      ...(audience.purchase.tempPaymentOfferId && {
        tempPaymentOfferId: audience.purchase.tempPaymentOfferId,
      }),
      ...(audience.purchase.tag && { tag: audience.purchase.tag }),
    };

    groups.push({
      title: audience.purchase.title,
      ...(audience.purchase.subtitle && {
        subtitle: audience.purchase.subtitle,
      }),
      offers: [offer],
    });
  }

  if (
    acquisitionTypes.includes(AcquisitionType.Subscription) &&
    audience.subscription?.offers?.length
  ) {
    groups.push({
      title: audience.subscription.title,
      ...(audience.subscription.subtitle && {
        subtitle: audience.subscription.subtitle,
      }),
      offers: audience.subscription.offers.map(o => ({
        basePaymentOfferId: o.basePaymentOfferId,
        targetMarket,
        ...(o.tempPaymentOfferId && {
          tempPaymentOfferId: o.tempPaymentOfferId,
        }),
        ...(o.tag && { tag: o.tag }),
      })),
    });
  }

  if (
    acquisitionTypes.includes(AcquisitionType.Rental) &&
    audience.rental?.offers?.length
  ) {
    groups.push({
      title: audience.rental.title,
      ...(audience.rental.subtitle && { subtitle: audience.rental.subtitle }),
      offers: audience.rental.offers.map(o => ({
        basePaymentOfferId: o.basePaymentOfferId,
        targetMarket,
        ...(o.tempPaymentOfferId && {
          tempPaymentOfferId: o.tempPaymentOfferId,
        }),
        ...(o.tag && { tag: o.tag }),
      })),
    });
  }

  return groups;
}

function buildConfigurationSection(
  configuration: MarketplaceRootData['configuration']
): MarketplaceOfferGroup[] {
  const offerGroups: MarketplaceOfferGroup[] = [];

  const audiences: { key: AudienceKey; targetMarket: TargetMarket }[] = [
    { key: 'subscriber', targetMarket: 'subscriber' },
    { key: 'freemium', targetMarket: 'freemium' },
  ];

  for (const { key, targetMarket } of audiences) {
    const audience = configuration[key];
    if (audience.enabled) {
      offerGroups.push(
        ...buildOfferGroupsFromAudience(
          audience,
          targetMarket,
          audience.acquisitionTypes
        )
      );
    }
  }

  return offerGroups;
}

function buildScheduleSection(
  schedule: MarketplaceRootData['schedule']
): string | null | undefined {
  if (schedule.publishType === 'now') return null;

  if (
    schedule.publishType === 'scheduled' &&
    dayjs.isDayjs(schedule.scheduledDate) &&
    dayjs.isDayjs(schedule.scheduledTime)
  ) {
    const date = schedule.scheduledDate.format('YYYY-MM-DD');
    const time = schedule.scheduledTime.format('HH:mm');
    // Interpreta como horário BRL (UTC-3) e converte para UTC
    return new Date(`${date}T${time}:00.000-03:00`).toISOString();
  }

  return undefined;
}

function buildContentSection(
  content: MarketplaceRootData['content'],
  creation: MarketplaceRootData['creation']
): MarketplaceProduct['productDetails'] {
  const copy: LandingPageCopySection[] = [
    {
      type: CopyType.UserAccess,
      title: 'Tenha acesso:',
      items: (content.benefits ?? []).filter(Boolean) as string[],
    },
    {
      type: CopyType.Reviews,
      title: content.reviewsSectionTitle || 'Opinião de quem já testou',
      reviews: (content.reviews ?? []) as LandingPageCopySection['reviews'],
    },
    ...(content.instructor?.name
      ? [
          {
            type: CopyType.Instructor,
            title: 'Conheça quem vai te ajudar',
            instructor: {
              name: content.instructor.name ?? '',
              ...(content.instructor.cref && { cref: content.instructor.cref }),
              ...(content.instructor.image?.preview && {
                image: content.instructor.image.preview,
              }),
            },
            ...(content.instructor.biography && {
              paragraphs: content.instructor.biography
                .split('\n')
                .filter(Boolean),
            }),
          } as LandingPageCopySection,
        ]
      : []),
  ];

  return {
    ...(creation.buttonText && { buttonText: creation.buttonText }),
    landingPageData: {
      ...(content.mediaType === 'video' &&
        content.teaser && { teaser: content.teaser }),
      ...(content.title && { title: content.title }),
      ...(content.durationTime && { durationTime: content.durationTime }),
      ...(content.programLength && {
        programs: { length: content.programLength },
      }),
      ...(content.level && { level: content.level }),
      ...(content.objective && { objective: content.objective }),
      ...(content.mainTitle && { title: content.mainTitle }),
      ...(content.description && { description: content.description }),
      ...(content.buttonText && { buttonText: content.buttonText }),
      copy,
    },
  };
}

function buildCreationSection(
  creation: MarketplaceRootData['creation'],
  productDetails: MarketplaceProduct['productDetails'],
  offerGroups: MarketplaceOfferGroup[],
  releaseDate: string | null | undefined
): MarketplacePayload {
  return {
    type: creation.productType,
    title: creation.title ?? '',
    ...(creation.description && { description: creation.description }),
    ...(creation.categoryIds?.length && { categoryIds: creation.categoryIds }),
    ...(creation.laneIds?.length && { laneIds: creation.laneIds }),
    ...(offerGroups.length && { offerGroups }),
    ...(releaseDate !== undefined && { releaseDate }),
    productDetails,
  };
}

export function buildMarketplacePayload(
  data: MarketplaceRootData
): MarketplacePayload {
  const { creation, configuration, content, schedule } = data;

  const offerGroups = buildConfigurationSection(configuration);
  const releaseDate = buildScheduleSection(schedule);
  const productDetails = buildContentSection(content, creation);

  return buildCreationSection(
    creation,
    productDetails,
    offerGroups,
    releaseDate
  );
}
